import { RGB, HSV } from '@rapoo/device-sdk'

/**
 * 通用工具：约束数值在指定范围内
 * @param value 要约束的数值
 * @param min 最小值
 * @param max 最大值
 * @returns 约束后的数值
 */
const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value))
}

/**
 * 将RGB颜色值转换为十六进制颜色字符串
 * @param rgb - 包含r、g、b三个属性的对象，分别代表红、绿、蓝三个颜色通道的值
 * @returns 返回十六进制颜色字符串，格式为#RRGGBB
 */
export function rgbToHex(rgb: RGB): string {
  // 约束RGB值在0-255，避免负数/超大值转Hex出现异常
  const r = clamp(Math.round(rgb.r), 0, 255)
  const g = clamp(Math.round(rgb.g), 0, 255)
  const b = clamp(Math.round(rgb.b), 0, 255)

  // 定义一个内部函数toHex，将数字转换为两位的十六进制字符串
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  // 使用模板字符串将三个颜色通道的值组合成十六进制颜色字符串
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * 将十六进制颜色值转换为RGB颜色对象
 * @param hex 十六进制颜色字符串，例如 "#FFFFFF" 或 "FFFFFF"
 * @returns 返回包含r、g、b三个属性的颜色对象
 */
export function hexToRgb(hex: string): RGB {
  // 过滤非法字符，只保留0-9、a-f、A-F，避免解析负数/特殊字符出错
  hex = hex.replace(/[^0-9a-fA-F#]/g, '').replace('#', '')

  // 修复3：处理非法Hex值（长度不足6位时补0，超过6位截断）
  if (hex.length === 0) hex = '000000'
  if (hex.length < 6) hex = hex.padEnd(6, '0')
  if (hex.length > 6) hex = hex.slice(0, 6)

  // 将十六进制字符串转换为十进制数字
  const bigint = parseInt(hex, 16)
  // 将十进制数字拆分为红、绿、蓝三个颜色通道的值（约束0-255）
  const r = clamp((bigint >> 16) & 255, 0, 255)
  const g = clamp((bigint >> 8) & 255, 0, 255)
  const b = clamp(bigint & 255, 0, 255)

  // 返回RGB颜色对象
  return { r, g, b }
}

/**
 * 将RGB颜色值转换为HSV颜色值
 * @param rgb - 包含r、g、b三个属性的RGB颜色对象
 * @returns 返回包含h、s、v三个属性的HSV颜色对象
 */
export function rgbToHsv(rgb: RGB): HSV {
  // 约束RGB值在0-255，避免归一化后出现负数/大于1的情况
  const r = clamp(rgb.r, 0, 255) / 255
  const g = clamp(rgb.g, 0, 255) / 255
  const b = clamp(rgb.b, 0, 255) / 255

  // 找出RGB值中的最大值和最小值
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  // 初始化H、S、V值为0
  let h = 0,
    s = 0
  const v = max // V值取最大值
  // 计算饱和度S
  const d = max - min
  s = max === 0 ? 0 : d / max
  // 计算色相H
  if (max !== min) {
    // 根据最大值是哪个颜色通道来计算H值
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h *= 60 // 将H值转换为角度值（0-360度）
  }

  // 约束H在0-360，S/V在0-1，避免异常值
  return {
    h: clamp(h, 0, 360),
    s: clamp(s, 0, 1),
    v: clamp(v, 0, 1),
  }
}

/**
 * 将HSV颜色空间转换为RGB颜色空间
 * @param hsv - HSV颜色对象，包含色调(h)、饱和度(s)和明度(v)属性
 * @returns RGB颜色对象，包含红(r)、绿(g)和蓝(b)属性
 */
export function hsvToRgb(hsv: HSV): RGB {
  // 约束HSV值的合法范围（H:0-360, S/V:0-1）
  const h = clamp(hsv.h, 0, 360)
  const s = clamp(hsv.s, 0, 1)
  const v = clamp(hsv.v, 0, 1)

  let r, g, b // 声明RGB三个通道的变量
  // 计算色相所在的区间索引（0-5）
  const i = Math.floor(h / 60)
  // 计算色相在当前区间内的相对位置（0-1）
  const f = h / 60 - i
  // 计算辅助值p：v * (1 - s)
  const p = v * (1 - s)
  // 计算辅助值q：v * (1 - f * s)
  const q = v * (1 - f * s)
  // 计算辅助值t：v * (1 - (1 - f) * s)
  const t = v * (1 - (1 - f) * s)

  // 根据色相所在的不同区间，计算RGB值
  switch (i % 6) {
    case 0: // 红色区间
      r = v
      g = t
      b = p
      break
    case 1: // 黄色区间
      r = q
      g = v
      b = p
      break
    case 2: // 绿色区间
      r = p
      g = v
      b = t
      break
    case 3: // 青色区间
      r = p
      g = q
      b = v
      break
    case 4: // 蓝色区间
      r = t
      g = p
      b = v
      break
    case 5: // 紫色区间
      r = v
      g = p
      b = q
      break
    default: // 默认情况（理论上不会执行）
      r = 0
      g = 0
      b = 0
      break
  }

  // RGB值约束在0-255，避免负数/超大值
  return {
    r: clamp(Math.round(r * 255), 0, 255),
    g: clamp(Math.round(g * 255), 0, 255),
    b: clamp(Math.round(b * 255), 0, 255),
  }
}

/**
 * 计算图片整体感知亮度，返回 0(全黑) ~ 255(全白)
 * 缩小采样到 32x32
 */
export async function getImageBrightness(src: string, sampleSize = 32): Promise<number> {
  const img = new Image()
  img.crossOrigin = 'anonymous'

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = (e) => reject(e)
    img.src = src
  })

  const canvas = document.createElement('canvas')
  canvas.width = sampleSize
  canvas.height = sampleSize
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  ctx.drawImage(img, 0, 0, sampleSize, sampleSize)

  let pixels: Uint8ClampedArray
  try {
    pixels = ctx.getImageData(0, 0, sampleSize, sampleSize).data
  } catch {
    // 跨域未配置 CORS，canvas 被污染读不出像素
    throw new Error('无法读取像素数据，可能是图片跨域未配置')
  }

  let total = 0
  for (let i = 0; i < pixels.length; i += 4) {
    // 感知亮度公式(0.299R + 0.587G + 0.114B)
    total += 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]
  }

  return total / (pixels.length / 4)
}
